import { Injectable } from '@angular/core';
import { UserManager, UserManagerSettings, User } from 'oidc-client-ts';
import { BehaviorSubject, Observable } from 'rxjs';
import { OIDCConfigService } from './oidc-config.service';
import config from '../../../assets/config.json';
@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private manager: UserManager;
  private authState: BehaviorSubject<boolean>;
  private currentUser: User | null = null;
  private userPermissions: string[] = [];
  private user$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  private userGroups: any = [];
  private adminGroups: any = [];
  private MFALoginKeyName = config.MFALoginKeyName;
  $authenticationState: Observable<boolean>;
  private authCompletedSubject = new BehaviorSubject<boolean>(false);
  authCompleted$ = this.authCompletedSubject.asObservable(); // Observable for authentication completion
  constructor(private _oidcConfigServce: OIDCConfigService) {
    const groupsConfig = { ...config };
    this.userGroups = groupsConfig.userGroups.split(',');
    this.adminGroups = groupsConfig.adminGroups.split(',');
    const clientSettings = this.getClientSettings(_oidcConfigServce);
    this.manager = new UserManager(clientSettings);
    this.authState = new BehaviorSubject(false);
    this.$authenticationState = this.authState.asObservable();
    // Load the user on initialization
    this.manager.getUser().then((user) => {
      this.currentUser = user;
      this.user$.next(user);
    });
    this.manager.events.addUserLoaded((user) => {
      this.user$.next(user);
    });

    this.manager.events.addUserUnloaded(() => {
      this.user$.next(null);
    });
  }
  /// Returns an observable for the authentication state
  public getUserObservable() {
    return this.user$.asObservable();
  }

  public getCurrentUserEmail(): string {
    return this.currentUser?.profile.email ?? '';
  }

  public getCurrentUserName(): string {
    const profile = this.currentUser?.profile;
    const name = profile?.name ?? `${profile?.given_name} ${profile?.family_name}`;
    return profile ? name : '';
  }

  public configIsValid(): boolean {
    return this._oidcConfigServce.configIsValid;
  }

  public async isLoggedIn(): Promise<boolean> {
    return this.manager.getUser().then((user) => {
      this.currentUser = user;

      return this.currentUser != null && !this.currentUser.expired;
    });
  }

  public isAuthenticated(): Promise<boolean> {
    return this.manager.getUser().then((user: User | null) => {
      return user != null && !user.expired;
    });
  }

  public getUserId(): string {
    const claims = this.getClaims();
    return claims?.sub ?? '';
  }

  public getClaims(): any {
    return this.currentUser?.profile;
  }

  public getAccessToken(): Promise<string> {
    return this.manager.getUser().then((user: User | null) => {
      if (user == null) {
        return '';
      }

      return this.currentUser!.access_token;
    });
  }

  public getAuthorizationHeaderValue(): string {
    return `${this.currentUser!.token_type} ${this.currentUser!.access_token}`;
  }

  public startAuthentication(): Promise<void> {
    return this.manager.signinRedirect();
  }

  public async getIsAuthorized(): Promise<boolean> {
    return this.manager.getUser().then(async (user: User | null) => {
      if (user === null) {
        this.manager.signinRedirect();
      }
      this.currentUser = user;

      this.userPermissions = this.getFormattedGroups();
      let isAuthorized = false;

      await this.isUser().then(async (isUser: boolean) => {
        isAuthorized = isUser;
      });

      if (user == null || !isAuthorized) {
        // Either no user found or the user is not authorized to use the app
        return false;
      }

      if (!user.expired) {
        return true;
      }

      return false;
    });
  }
  public async getIsAdminAuthorized(): Promise<boolean> {
    return this.manager.getUser().then(async (user: User | null) => {
      if (user === null) {
        // temporary fix till the time velocity page is ready
        // this.manager.signinRedirect();
      }
      this.currentUser = user;

      this.userPermissions = this.getFormattedGroups();
      let isAuthorized = false;

      await this.isAdmin().then(async (isUser: boolean) => {
        isAuthorized = isUser;
      });

      if (user == null || !isAuthorized) {
        // Either no user found or the user is not authorized to use the app
        return false;
      }

      if (!user.expired) {
        return true;
      }

      return false;
    });
  }
  public async isUser(): Promise<boolean> {
    const data = await this.getProfileGroups().then((claims: string[]) => {
      for (let i = 0; i < this.userGroups.length; i++) {
        for (let j = 0; j < claims.length; j++) {
          if (this.userGroups[i].toLowerCase() === claims[j].toLowerCase()) {
            return true;
          }
        }
      }
      return false;
    });
    return data;
  }

  public async isAdmin(): Promise<boolean> {
    const data = await this.getProfileGroups().then((claims: string[]) => {
      for (let i = 0; i < this.adminGroups.length; i++) {
        for (let j = 0; j < claims.length; j++) {
          if (this.adminGroups[i].toLowerCase() === claims[j].toLowerCase()) {
            return true;
          }
        }
      }
      return false;
    });
    return data;
  }

  public completeAuthentication(): Promise<void> {
    this.manager.events.addAccessTokenExpiring(() => {});

    return this.manager.signinRedirectCallback().then((user: User | null) => {
      this.currentUser = user;
      this.authState.next(true);
      this.authCompletedSubject.next(true);
    });
  }

  public startLogout(): Promise<void> {
    this.manager.events.removeAccessTokenExpiring(() => {});
    sessionStorage.clear();
    localStorage.clear();
    const MFALoginKey: string | undefined = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${this.MFALoginKeyName}=`))
      ?.split('=')[1];
    if (MFALoginKey && !atob(MFALoginKey).endsWith(this.MFALoginKeyName)) {
      document.cookie = `${this.MFALoginKeyName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure;`;
    }
    return this.manager.signoutRedirect();
  }

  public completeLogout(): Promise<void> {
    return this.manager.signoutRedirectCallback().then(() => {
      this.currentUser = null;
      this.authState.next(false);
    });
  }

  public silentCallback(): void {
    this.manager.signinSilentCallback().catch((_err: any) => {});
  }

  private async getProfileGroups(): Promise<string[]> {
    if (this.userPermissions.length > 0) {
      return this.userPermissions;
    }
    const data = await this.manager.getUser().then((user: User | null) => {
      const key = 'groups';
      this.userPermissions = (user?.profile[key] as string[]) ?? [];
      return this.userPermissions;
    });
    return data;
  }

  public getFormattedGroups(): string[] {
    const groups: string[] = [];
    const key = 'groups';
    const profileGroups = this.currentUser?.profile[key] as string[];
    if (!profileGroups) {
      return [];
    }

    let userGroupClaims: string[] = [];

    if (profileGroups.length > 0) {
      if (typeof profileGroups === 'string') {
        userGroupClaims = [profileGroups];
      } else {
        userGroupClaims = [...profileGroups];
      }

      // RegEx pattern:
      //   Group name starts with "CN=", may contain dashes ("-"), ends at first comma (","), and is case insensitive
      const groupPattern = /CN=(\w[\w-]+),/i;
      for (let i = 0; i < userGroupClaims.length; i++) {
        const matches = userGroupClaims[i].match(groupPattern);
        if (matches) {
          groups.push(matches[1]);
        }
      }
    }

    return groups;
  }

  private getClientSettings(configService: OIDCConfigService): UserManagerSettings {
    const config = configService.getConfiguration();
    return {
      authority: config?.authority,
      client_id: config?.client_id,
      client_secret: config?.client_secret,
      redirect_uri: config?.redirect_uri,
      post_logout_redirect_uri: config?.post_logout_redirect_uri,
      response_type: config?.response_type,
      scope: config?.scope,
      filterProtocolClaims: true,
      loadUserInfo: true,
      silent_redirect_uri: window.location.origin + +'/assets/silent-renew.html',
      metadata: {
        issuer: config?.issuer,
        jwks_uri: config?.jwks_uri,
        end_session_endpoint: config?.end_session_endpoint,
        authorization_endpoint: config?.authorization_endpoint,
        userinfo_endpoint: config?.userinfo_endpoint,
        grant_types_supported: config?.grant_types_supported,
        token_endpoint: config?.token_endpoint,
      },
    };
  }
  getUrl(): { path: string; queryParams: Record<string, string> } | null {
    const requestedURL = sessionStorage.getItem('RequestedURL');
    if (requestedURL) {
      const url = new URL(requestedURL, window.location.origin);
      const path = url.pathname;
      const queryParams: Record<string, string> = {};
      url.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });
      return { path, queryParams };
    }
    return null;
  }
}
